import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DatePicker from '../../components/DatePicker/DatePicker';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/FormPage.css';

const emptyItem = {
    description: '',
    unit: 'SQ FT',
    quantity: '',
    rate: '',
    amount: ''
};

function CreateQuotation() {
    const { quotationId } = useParams();
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [items, setItems] = useState([]);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showItemModal, setShowItemModal] = useState(false);
    const [termsAndConditions, setTermsAndConditions] = useState([]);
    const [selectedTerms, setSelectedTerms] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [quotationTermIds, setQuotationTermIds] = useState([]);

    const [quotationDetails, setQuotationDetails] = useState({
        quotationNo: '',
        date: new Date().toISOString().split('T')[0],
        quotationTo: '',
        mobilenumber: '',
        kindattention: '',
        subject: '',
        status: 'Pending'
    });
    const [currentItem, setCurrentItem] = useState(emptyItem);
    const { isLoading, withLoading } = usePageLoading(true);

    useEffect(() => {
        withLoading(async () => {
            try {
                const termsPromise = api.get('/termsandcondition');
                const quotationPromise = quotationId
                    ? api.get(`/quotations/${quotationId}`)
                    : null;

                const termsResponse = await termsPromise;
                setTermsAndConditions(termsResponse.data);

                if (quotationPromise) {
                    const response = await quotationPromise;
                    const date = new Date(response.data.date);
                    const formattedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                        .toISOString()
                        .split('T')[0];

                    setItems(response.data.items.map(item => ({
                        ...item,
                        amount: (parseFloat(item.quantity) * parseFloat(item.rate)).toFixed(2)
                    })));

                    setQuotationDetails({
                        quotationNo: response.data.quotationNo || '',
                        date: formattedDate,
                        quotationTo: response.data.quotationTo || '',
                        mobilenumber: response.data.mobilenumber || '',
                        kindattention: response.data.kindattention || '',
                        subject: response.data.subject || '',
                        status: response.data.status || 'Pending'
                    });

                    if (response.data.termIds && response.data.termIds.length > 0) {
                        setQuotationTermIds(response.data.termIds);
                    }
                }
            } catch (error) {
                console.error('Error loading quotation page:', error);
                const message = error.response?.data?.Error
                    || error.response?.data?.message
                    || error.message
                    || 'Failed to load quotation';
                if (quotationId) {
                    alert(message);
                } else {
                    setTermsAndConditions([]);
                }
            }
        });
    }, [quotationId, withLoading]);

    useEffect(() => {
        if (termsAndConditions.length > 0 && quotationTermIds.length > 0) {
            const selected = termsAndConditions.filter(term =>
                quotationTermIds.includes(term.id)
            );
            setSelectedTerms(selected);
            setSelectAll(selected.length === termsAndConditions.length);
        }
    }, [termsAndConditions, quotationTermIds]);

    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        if (!selectAll) {
            setSelectedTerms(termsAndConditions);
        } else {
            setSelectedTerms([]);
        }
    };

    const handleTermsSelection = (term) => {
        setSelectedTerms(prev => {
            const isSelected = prev.find(t => t.id === term.id);
            const newSelection = isSelected
                ? prev.filter(t => t.id !== term.id)
                : [...prev, term];
            setSelectAll(newSelection.length === termsAndConditions.length);
            return newSelection;
        });
    };

    const handleRemoveTerm = (termId) => {
        setSelectedTerms(prev => prev.filter(term => term.id !== termId));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentItem(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'quantity' || name === 'rate') {
                const quantity = name === 'quantity' ? value : prev.quantity;
                const rate = name === 'rate' ? value : prev.rate;
                updated.amount = (parseFloat(quantity || 0) * parseFloat(rate || 0)).toFixed(2);
            }
            return updated;
        });
    };

    const openAddItemModal = () => {
        setCurrentItem({ ...emptyItem });
        setIsEditing(false);
        setEditingIndex(-1);
        setShowItemModal(true);
    };

    const openEditItemModal = (index) => {
        setCurrentItem({
            ...items[index],
            unit: items[index].unit || 'SQ FT'
        });
        setEditingIndex(index);
        setIsEditing(true);
        setShowItemModal(true);
    };

    const closeItemModal = () => {
        setShowItemModal(false);
        setCurrentItem({ ...emptyItem });
        setIsEditing(false);
        setEditingIndex(-1);
    };

    const handleAddItem = (e) => {
        e.preventDefault();
        if (!currentItem.description || !currentItem.quantity || !currentItem.rate) {
            alert('Please fill in all required fields');
            return;
        }

        if (isEditing) {
            setItems(prev => prev.map((item, index) =>
                index === editingIndex ? { ...currentItem, srNo: index + 1 } : item
            ));
        } else {
            setItems(prev => [...prev, { ...currentItem, srNo: prev.length + 1 }]);
        }

        closeItemModal();
    };

    const handleDeleteItem = (index) => {
        if (!window.confirm('Remove this item?')) return;
        setItems(prev => prev
            .filter((_, i) => i !== index)
            .map((item, i) => ({ ...item, srNo: i + 1 }))
        );
    };

    const handleQuotationDetailsChange = (e) => {
        const { name, value } = e.target;
        setQuotationDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const saveQuotation = async () => {
        if (items.length === 0) {
            alert('Please add at least one item');
            return;
        }

        try {
            const data = {
                ...quotationDetails,
                items,
                termIds: selectedTerms.map(term => term.id)
            };
            if (quotationId) {
                await api.put(`/quotations/${quotationId}`, data);
                alert('Quotation updated successfully');
            } else {
                await api.post('/quotations', data);
                alert('Quotation saved successfully');
            }
        } catch (error) {
            console.error('Error saving quotation:', error);
            const message = error.response?.data?.Error
                || error.response?.data?.message
                || error.message
                || 'Failed to save quotation';
            alert(message);
        }
    };

    const generatePDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;

        const checkAndAddPage = (currentY, requiredSpace) => {
            if (currentY + requiredSpace > pageHeight - 20) {
                doc.addPage();
                return 20;
            }
            return currentY;
        };

        const logoImg = new Image();
        logoImg.src = '/assets/companyLogo.png';
        const logoWidth = 80;
        const logoHeight = 35;
        const logoX = (pageWidth - logoWidth) / 2;
        doc.addImage(logoImg, 'PNG', logoX, 0, logoWidth, logoHeight);

        doc.setDrawColor(0);
        doc.setLineWidth(0.5);
        doc.line(15, 35, 195, 35);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const leftMargin = 15;
        const rightMargin = 195;

        doc.text(`Quotation To: ${quotationDetails.quotationTo}`, leftMargin, 45);
        doc.text(`Mobile: ${quotationDetails.mobilenumber}`, leftMargin, 52);
        doc.text(`Kind Attn: ${quotationDetails.kindattention}`, leftMargin, 59);

        doc.text(`Quotation No: ${quotationDetails.quotationNo}`, rightMargin, 45, { align: 'right' });
        doc.text(`Date: ${formatDateDDMMYYYY(quotationDetails.date)}`, rightMargin, 52, { align: 'right' });

        doc.setFont('helvetica', 'bold');
        doc.text('Subject:', leftMargin, 70);
        doc.setFont('helvetica', 'normal');
        doc.text(quotationDetails.subject, leftMargin + 20, 70);

        let finalY = 80;

        autoTable(doc, {
            startY: finalY,
            head: [['Sr No', 'Item Description', 'Unit', 'Quantity', 'Rate', 'Amount']],
            body: items.map(item => [
                item.srNo,
                item.description,
                item.unit,
                item.quantity,
                item.rate,
                item.amount
            ]),
            styles: {
                fontSize: 9,
                cellPadding: 3,
                overflow: 'linebreak',
                valign: 'top'
            },
            headStyles: {
                fillColor: [34, 34, 34],
                valign: 'middle'
            },
            columnStyles: {
                0: { cellWidth: 14 },
                1: { cellWidth: 78 },
                2: { cellWidth: 20 },
                3: { cellWidth: 22 },
                4: { cellWidth: 22 },
                5: { cellWidth: 24 }
            },
            didDrawPage: function (data) {
                finalY = data.cursor.y;
            }
        });

        finalY = checkAndAddPage(finalY, 10);
        doc.setFont('helvetica', 'bold');
        const total = items.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(2);
        doc.text(`Total: ${total}`, 195, finalY + 10, { align: 'right' });
        finalY += 15;

        if (selectedTerms.length > 0) {
            finalY = checkAndAddPage(finalY, 25);
            doc.setFont('helvetica', 'bold');
            doc.text('Terms and Conditions:', 15, finalY + 20);

                autoTable(doc, {
                    startY: finalY + 10,
                    head: [['Sr No', 'Terms and Conditions']],
                    body: selectedTerms.map((term, index) => [
                        index + 1,
                        term.description
                    ]),
                    styles: {
                        fontSize: 9,
                        cellPadding: 3,
                        overflow: 'linebreak',
                        valign: 'top'
                    },
                    headStyles: {
                        fillColor: [34, 34, 34],
                        valign: 'middle'
                    },
                    columnStyles: {
                        0: { cellWidth: 20 },
                        1: { cellWidth: 'auto' }
                    },
                    didDrawPage: function (data) {
                        finalY = data.cursor.y;
                    }
                });
        }

        finalY = checkAndAddPage(finalY, 10);

        doc.setLineWidth(0.5);
        doc.line(15, finalY, pageWidth - 15, finalY);

        doc.setFontSize(10);
        doc.text("Shivam Buildcare", pageWidth / 2, finalY + 5, { align: 'center' });
        doc.setFontSize(8);
        const addressText = 'B-531 Money Plant High Street Jagatpur Road, Sarkhej-Gandhinagar Hwy,';
        doc.text(addressText, pageWidth / 2, finalY + 9, { align: 'center' });
        doc.text('Nr BSNL Office, Gota Ahmedabad Gujarat - 382470', pageWidth / 2, finalY + 13, { align: 'center' });

        const iconPhone = new Image();
        iconPhone.src = process.env.PUBLIC_URL + '/assets/iconPhone.png';
        const phoneTextWidth = doc.getTextWidth('+91 99793 63351');
        const phoneIconX = (pageWidth / 2) - (phoneTextWidth / 2) - 4;
        doc.addImage(iconPhone, 'PNG', phoneIconX, finalY + 16, 2, 2);
        doc.text('+91 99793 63351', pageWidth / 2, finalY + 18, { align: 'center' });

        const iconEmail = new Image();
        iconEmail.src = process.env.PUBLIC_URL + '/assets/iconEmail.png';
        const emailTextWidth = doc.getTextWidth('shivambuildcare@gmail.com');
        const emailIconX = (pageWidth / 2) - (emailTextWidth / 2) - 4;
        doc.addImage(iconEmail, 'PNG', emailIconX, finalY + 20, 2, 2);
        doc.text('shivambuildcare@gmail.com', pageWidth / 2, finalY + 22, { align: 'center' });

        doc.save('quotation.pdf');
    };

    const itemsTotal = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2);

    return (
        <>
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{quotationId ? 'Edit quotation' : 'Add quotation'}</h1>
                        <p className="form-page-subtitle">
                            Fill quotation details, add items, and attach terms
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/quotations')}
                        >
                            Back to list
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Quotation details</h2>
                            <p className="form-page-section-hint">Recipient and document information</p>
                        </div>
                    </div>
                    <div className="form-page-grid cols-3">
                        <div className="form-page-field">
                            <label htmlFor="quotationNo">Quotation no</label>
                            <input
                                type="text"
                                id="quotationNo"
                                name="quotationNo"
                                value={quotationDetails.quotationNo}
                                onChange={handleQuotationDetailsChange}
                                placeholder="Enter quotation number"
                            />
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="date">Date</label>
                            <DatePicker
                                id="date"
                                name="date"
                                value={quotationDetails.date}
                                onChange={handleQuotationDetailsChange}
                                required
                            />
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="status">Status</label>
                            <select
                                id="status"
                                name="status"
                                value={quotationDetails.status}
                                onChange={handleQuotationDetailsChange}
                            >
                                <option value="Pending">Pending</option>
                                <option value="Approved">Approved</option>
                                <option value="Rejected">Rejected</option>
                            </select>
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="quotationTo">Quotation to</label>
                            <input
                                type="text"
                                id="quotationTo"
                                name="quotationTo"
                                value={quotationDetails.quotationTo}
                                onChange={handleQuotationDetailsChange}
                                placeholder="Enter recipient name"
                            />
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="mobilenumber">Mobile number</label>
                            <input
                                type="tel"
                                id="mobilenumber"
                                name="mobilenumber"
                                value={quotationDetails.mobilenumber}
                                onChange={handleQuotationDetailsChange}
                                placeholder="Enter mobile number"
                            />
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="kindattention">Kind attention</label>
                            <input
                                type="text"
                                id="kindattention"
                                name="kindattention"
                                value={quotationDetails.kindattention}
                                onChange={handleQuotationDetailsChange}
                                placeholder="Enter kind attention"
                            />
                        </div>
                        <div className="form-page-field span-full">
                            <label htmlFor="subject">Subject</label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={quotationDetails.subject}
                                onChange={handleQuotationDetailsChange}
                                placeholder="Enter subject"
                            />
                        </div>
                    </div>
                </section>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Quotation items</h2>
                            <p className="form-page-section-hint">
                                {items.length} item{items.length === 1 ? '' : 's'} on this quotation
                            </p>
                        </div>
                        <button type="button" className="form-page-btn" onClick={openAddItemModal}>
                            Add item
                        </button>
                    </div>

                    {items.length === 0 ? (
                        <div className="form-page-empty">
                            <h3>No items yet</h3>
                            <p>Add items worked on to add to this quotation.</p>
                            <button type="button" className="form-page-btn" onClick={openAddItemModal}>
                                Add first item
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="form-page-table-wrap">
                                <table className="form-page-table">
                                    <thead>
                                        <tr>
                                            <th className="col-sr">Sr No</th>
                                            <th className="col-desc">Description</th>
                                            <th className="col-unit">Unit</th>
                                            <th className="col-num">Qty</th>
                                            <th className="col-num">Rate</th>
                                            <th className="col-num">Amount</th>
                                            <th className="col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={index}>
                                                <td className="col-sr">{item.srNo}</td>
                                                <td className="col-desc">{item.description}</td>
                                                <td className="col-unit">{item.unit}</td>
                                                <td className="col-num">{item.quantity}</td>
                                                <td className="col-num">{item.rate}</td>
                                                <td className="col-num">{item.amount}</td>
                                                <td className="col-actions">
                                                    <div className="row-actions">
                                                        <button
                                                            type="button"
                                                            className="form-page-btn-secondary small"
                                                            onClick={() => openEditItemModal(index)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="form-page-btn-danger small"
                                                            onClick={() => handleDeleteItem(index)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ marginTop: '0.85rem', fontWeight: 650, textAlign: 'right' }}>
                                Total: {itemsTotal}
                            </div>
                        </>
                    )}
                </section>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Terms and conditions</h2>
                            <p className="form-page-section-hint">
                                {selectedTerms.length} term{selectedTerms.length === 1 ? '' : 's'} selected
                            </p>
                        </div>
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => setShowTermsModal(true)}
                        >
                            Select terms
                        </button>
                    </div>

                    {selectedTerms.length === 0 ? (
                        <div className="form-page-empty">
                            <h3>No terms selected</h3>
                            <p>Optionally attach terms and conditions to this quotation.</p>
                            <button
                                type="button"
                                className="form-page-btn-secondary"
                                onClick={() => setShowTermsModal(true)}
                            >
                                Select terms
                            </button>
                        </div>
                    ) : (
                        <div className="form-page-table-wrap">
                            <table className="form-page-table">
                                <thead>
                                        <tr>
                                            <th className="col-sr">#</th>
                                            <th className="col-desc">Description</th>
                                            <th className="col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedTerms.map((term, index) => (
                                            <tr key={term.id}>
                                                <td className="col-sr">{index + 1}</td>
                                                <td className="col-desc">{term.description}</td>
                                                <td className="col-actions">
                                                    <button
                                                        type="button"
                                                        className="form-page-btn-danger small"
                                                        onClick={() => handleRemoveTerm(term.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                <div className="document-page-footer-actions">
                    <button
                        type="button"
                        className="form-page-btn-secondary"
                        onClick={generatePDF}
                    >
                        Generate PDF
                    </button>
                    <button
                        type="button"
                        className="form-page-btn"
                        onClick={saveQuotation}
                    >
                        {quotationId ? 'Update quotation' : 'Save quotation'}
                    </button>
                </div>
            </div>
        </PageLoadingArea>

            {showItemModal && (
                <div className="form-page-modal-overlay" onClick={closeItemModal}>
                    <div className="form-page-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="form-page-modal-head">
                            <div>
                                <h2>{isEditing ? 'Edit item' : 'Add item'}</h2>
                                <p>Amount is calculated from quantity × rate</p>
                            </div>
                            <button type="button" className="form-page-btn-ghost" onClick={closeItemModal}>
                                Close
                            </button>
                        </div>

                        <form id="quotation-item-form" onSubmit={handleAddItem}>
                            <div className="form-page-grid">
                                <div className="form-page-field span-2">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={currentItem.description}
                                        onChange={handleInputChange}
                                        rows="3"
                                        required
                                        autoFocus
                                        placeholder="Describe the work or material"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="unit">Unit</label>
                                    <select
                                        id="unit"
                                        name="unit"
                                        value={currentItem.unit}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="SQ FT">SQ FT</option>
                                        <option value="SQ MTR">SQ MTR</option>
                                        <option value="NOS">NOS</option>
                                    </select>
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="quantity">Quantity</label>
                                    <input
                                        type="number"
                                        id="quantity"
                                        name="quantity"
                                        value={currentItem.quantity}
                                        onChange={handleInputChange}
                                        required
                                        step="any"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="rate">Rate</label>
                                    <input
                                        type="number"
                                        id="rate"
                                        name="rate"
                                        value={currentItem.rate}
                                        onChange={handleInputChange}
                                        required
                                        step="any"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="amount">Amount</label>
                                    <input
                                        type="text"
                                        id="amount"
                                        value={currentItem.amount}
                                        readOnly
                                    />
                                </div>
                            </div>
                        </form>

                        <div className="form-page-modal-foot">
                            <button type="button" className="form-page-btn-secondary" onClick={closeItemModal}>
                                Cancel
                            </button>
                            <button type="submit" form="quotation-item-form" className="form-page-btn">
                                {isEditing ? 'Update item' : 'Add item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showTermsModal && (
                <div className="form-page-modal-overlay" onClick={() => setShowTermsModal(false)}>
                    <div className="form-page-modal wide" onClick={(e) => e.stopPropagation()}>
                        <div className="form-page-modal-head">
                            <div>
                                <h2>Select terms and conditions</h2>
                                <p>Choose the terms to include on this quotation</p>
                            </div>
                            <button
                                type="button"
                                className="form-page-btn-ghost"
                                onClick={() => setShowTermsModal(false)}
                            >
                                Close
                            </button>
                        </div>

                        <div className="form-page-select-all">
                            <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                id="select-all"
                            />
                            <label htmlFor="select-all">Select all</label>
                        </div>

                        <div className="form-page-terms-list">
                            {termsAndConditions.length === 0 ? (
                                <p style={{ color: '#888', margin: 0 }}>No terms available yet.</p>
                            ) : termsAndConditions.map(term => (
                                <label key={term.id} className="form-page-term-item">
                                    <input
                                        type="checkbox"
                                        checked={selectedTerms.some(t => t.id === term.id)}
                                        onChange={() => handleTermsSelection(term)}
                                    />
                                    <span>{term.description}</span>
                                </label>
                            ))}
                        </div>

                        <div className="form-page-modal-foot">
                            <button
                                type="button"
                                className="form-page-btn-secondary"
                                onClick={() => setShowTermsModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="form-page-btn"
                                onClick={() => setShowTermsModal(false)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CreateQuotation;
